/**
 * The chrome every admin screen sits inside: who signed in, where this is
 * running, and what currently needs attention. Every figure is a count of rows.
 *
 * This file names no platform, so the Pages Function can import the contract
 * rather than declare it twice. See OBS_HANDOFF.md section 2.
 */

export type ConsoleCounts = {
  readonly readerStuck: number;
  readonly outboxWaiting: number;
  readonly passesExpiring: number;
  readonly vouchersExhausted: number;
  readonly plans: number;
  readonly ledgerRows: number;
  readonly candidates: number;
};

export type ConsolePayload = {
  readonly ok: true;
  readonly admin: string;
  readonly environment: string;
  readonly currency: string;
  readonly gateway: { readonly name: string; readonly movesRealMoney: boolean };
  readonly counts: ConsoleCounts;
  readonly readAt: number;
};

export type Alert = {
  readonly key: string;
  readonly label: string;
  readonly detail: string;
  /** Null where what needs attention is a state rather than a tally. */
  readonly count: number | null;
  readonly href: string;
};

export const SCREENS: readonly { readonly href: string; readonly label: string; readonly hint: string }[] =
  [
    {
      href: "/observability",
      label: "Observability",
      hint: "Signups, reach, the model pins that ran, the event log and reader health",
    },
    {
      href: "/candidates",
      label: "Candidates",
      hint: "Every account, its counts, its pass, and the record behind it",
    },
    {
      href: "/payments",
      label: "Payments",
      hint: "Plans, the ledger, refunds, vouchers and granted passes, in rupees",
    },
    {
      href: "/models",
      label: "Models",
      hint: "Which model read what, how often it failed, and the prompts it was given",
    },
    {
      href: "/audit",
      label: "Audit log",
      hint: "What an admin did, to whom, and when",
    },
  ];

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Only things that are true right now. An empty list is the ordinary case. */
export function alertsOf(counts: ConsoleCounts): readonly Alert[] {
  const out: Alert[] = [];
  if (counts.readerStuck > 0) {
    out.push({
      key: "reader_stuck",
      label: `${plural(counts.readerStuck, "row", "rows")} the model cannot read`,
      detail: "A debrief or job description that has burned attempts and is still unread.",
      count: counts.readerStuck,
      href: "/observability",
    });
  }
  if (counts.outboxWaiting > 0) {
    out.push({
      key: "outbox_waiting",
      label: `${plural(counts.outboxWaiting, "event", "events")} waiting on the outbox`,
      detail: "Written to the event log and not published yet.",
      count: counts.outboxWaiting,
      href: "/observability",
    });
  }
  if (counts.passesExpiring > 0) {
    out.push({
      key: "passes_expiring",
      label: `${plural(counts.passesExpiring, "pass", "passes")} ${
        counts.passesExpiring === 1 ? "ends" : "end"
      } within 14 days`,
      detail: "Counted from commerce_subscription, not estimated.",
      count: counts.passesExpiring,
      href: "/candidates",
    });
  }
  if (counts.vouchersExhausted > 0) {
    out.push({
      key: "vouchers_exhausted",
      label: `${plural(counts.vouchersExhausted, "voucher has", "vouchers have")} run out`,
      detail: "Used as many times as it was allowed.",
      count: counts.vouchersExhausted,
      href: "/payments",
    });
  }
  if (counts.plans === 0) {
    out.push({
      key: "plans_unpriced",
      label: "No plan has been priced",
      detail: "Nothing can be sold, granted or refunded until a plan exists.",
      count: null,
      href: "/payments",
    });
  }
  return out;
}

/**
 * THREE STATES, NOT TWO. `movesRealMoney` false used to mean the mock, because
 * the mock was the only thing that could be false. Razorpay in test mode is
 * also false and is not a mock: it is the real gateway, reachable, refusing
 * real cards. Reading the boolean alone produced "Mock gateway: razorpay",
 * which contradicts itself in four words.
 */
export type GatewayMode = "mock" | "test" | "live";

/** The same three states the candidate Plans screen reads. Kept in step. */
export function gatewayModeOf(gateway: {
  name: string;
  movesRealMoney: boolean;
} | null | undefined): GatewayMode {
  if (gateway === null || gateway === undefined) return "mock";
  if (gateway.name !== "razorpay") return "mock";
  return gateway.movesRealMoney ? "live" : "test";
}

/**
 * WHAT A REFUND ON THIS ROW WILL ACTUALLY DO.
 *
 * Read off the row, not off whatever gateway is configured now. A ledger
 * written before Razorpay was wired in is full of rows marked `mock`, and they
 * stay refundable only as mocks for ever. The server refuses a mismatch with
 * `gateway_mismatch`; this is the same fact said to the operator beforehand.
 */
export function refundLabel(paymentGateway: string, mode: GatewayMode): string {
  if (paymentGateway !== "razorpay") return "Record a mock refund";
  return mode === "live" ? "Refund" : "Refund a test payment";
}

export function refundWarning(paymentGateway: string, mode: GatewayMode): string {
  if (paymentGateway !== "razorpay") {
    return "No money moves. This updates refunded_paise on a row marked gateway = mock and writes an admin_audit row. Nobody is paid anything.";
  }
  if (mode === "live") return "This reaches a real bank and cannot be undone from this screen.";
  return "Razorpay is in test mode, so this calls the real refund API against a test payment. No money moves.";
}

export function gatewayLine(payload: ConsolePayload): string {
  const { name, movesRealMoney } = payload.gateway;
  if (movesRealMoney) return `Live gateway: ${name}`;
  if (name === "razorpay") return "Razorpay in test mode, no money moves";
  return `Mock gateway: ${name}, no money moves`;
}

export async function loadConsole(): Promise<ConsolePayload> {
  let response: Response;
  try {
    response = await fetch("/api/console", { headers: { accept: "application/json" } });
  } catch {
    throw new Error("unreachable");
  }
  const body = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || body === null || body.ok !== true) {
    throw new Error(body?.error ?? `http_${response.status}`);
  }
  return body as unknown as ConsolePayload;
}
